import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import {
  KeyboardAwareScrollView,
  KeyboardController
} from 'react-native-keyboard-controller';
import { Button, HelperText, Text, TextInput } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ErrorIcon, NotVerifiedIcon, VerifiedIcon } from '@/assets';
import type { PersonProfile } from '@/features/authorization/types/person';
import { AvatarWithMenu } from '@/features/media/components/menu/AvatarWithMenu';
import type { AvatarPickerResult } from '@/features/media/types/picker';
import { usePopup } from '@/features/popup/provider/usePopup';
import { SUPPORT_URL } from '@/features/profile-setting/constants/links';
import { MIN_NAME_LENGTH } from '@/features/profile-setting/constants/person';
import type { PersonUpdate } from '@/features/profile-setting/types/person';
import { isNameValid } from '@/features/profile-setting/utils/person';
import { useVerification } from '@/features/verification/providers/useVerification';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { getPublicName, isEmptyOrNull } from '@/shared/utils/string';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { EditMode } from '@/types';

const currentYear = new Date().getFullYear();

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    contentWrapper: {
      gap: 32,
      paddingHorizontal: 16,
      paddingBottom: 32 + insets.bottom
    },
    contentWrapperSticky: {
      gap: 32,
      paddingHorizontal: 16,
      paddingBottom: 32
    },
    footer: {
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 16 + insets.bottom,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    header: { alignItems: 'center', gap: 12 },
    description: { textAlign: 'center' },
    image: {
      paddingHorizontal: 48,
      justifyContent: 'center',
      alignItems: 'center'
    },
    inputs: { gap: 8 },
    input: { backgroundColor: theme.colors.surfaceContainerLow },
    publicName: { textAlign: 'center', paddingTop: 8, paddingBottom: 16 }
  });

export type Props = {
  mode?: EditMode;
  person?: PersonProfile;
  disableInput?: boolean;
  /**
   * Renders the submit button in a fixed footer below the scrollable content
   * instead of as the last scrollable element. Opt-in for flows (e.g. the
   * registration wizard) where long content on small screens would push the
   * button out of view.
   */
  stickyButton?: boolean;
  onChange: (personUpdate: PersonUpdate) => void;
  onSave: () => void;
};

export const ProfileEditor: FC<Props> = ({
  mode = EditMode.EDIT,
  person,
  disableInput,
  stickyButton = false,
  onChange,
  onSave
}) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'components.profile-editor'
  });

  const {
    showSuccessModal,
    showVerificationModal,
    isEmailVerificationEnabled,
    isEmailVerified
  } = useVerification();

  const isRegistrationMode = mode === EditMode.REGISTRATION;

  const [firstName, setFirstName] = useState<string | undefined>(
    person?.firstName
  );
  const [lastName, setLastName] = useState<string | undefined>(
    person?.lastName
  );
  const [birthYear, setBirthYear] = useState<number | undefined>(
    person?.birthYear
  );

  const [avatarResult, setAvatarResult] = useState<AvatarPickerResult>({
    logo: undefined,
    temporaryMediaItemId: undefined,
    localImage: undefined
  });

  const showPublicName = useMemo(
    () =>
      isRegistrationMode &&
      !isEmptyOrNull(firstName) &&
      firstName?.trim().length > 1 &&
      !isEmptyOrNull(lastName) &&
      lastName?.trim().length > 0,
    [firstName, isRegistrationMode, lastName]
  );

  useEffect(() => {
    onChange({
      firstName,
      lastName,
      birthYear,
      profilePicture: avatarResult.logo,
      temporaryMediaItemId: avatarResult.temporaryMediaItemId,
      localImage: avatarResult.localImage
    });
    // Note: passing onChange as dependency will cause a loop, using useEffectEvent (React 19.2) may fix it
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, birthYear, avatarResult]);

  /**
   * Allow in registration mode initial undefined
   * Show error only after user touched the field
   */
  const isFirstNameError =
    firstName === undefined ||
    firstName.trim().length < MIN_NAME_LENGTH ||
    !isNameValid(firstName);
  const showFirstNameError =
    !(isRegistrationMode && firstName === undefined) && isFirstNameError;

  const isLastNameError =
    lastName === undefined ||
    lastName.trim().length < MIN_NAME_LENGTH ||
    !isNameValid(lastName);
  const showLastNameError =
    !(isRegistrationMode && lastName === undefined) && isLastNameError;

  const isBirthError =
    birthYear === undefined || currentYear - birthYear < 16 || birthYear < 1900;
  const showBirthError =
    !(isRegistrationMode && birthYear === undefined) && isBirthError;

  const birthErrorMessage = useMemo(() => {
    if (birthYear === undefined) {
      return t('birthError.undefined');
    } else {
      return t(
        Number(birthYear ?? 0) < 1900 ? 'birthError.old' : 'birthError.young'
      );
    }
  }, [birthYear, t]);

  const canSave = useMemo(
    () => !isFirstNameError && !isLastNameError && !isBirthError,
    [isBirthError, isFirstNameError, isLastNameError]
  );

  const { showAlert } = usePopup();

  const handleChangeFiledData = useCallback(
    (field: 'email') => {
      KeyboardController.dismiss();
      showAlert({
        dismissable: true,
        labels: t('alerts.change', {
          returnObjects: true,
          field: t(field)
        }),
        onConfirm: () => handleOpenUrl(SUPPORT_URL)
      });
    },
    [showAlert, t]
  );

  const submitButton = isRegistrationMode ? (
    <Button disabled={!canSave} mode="contained" onPress={onSave}>
      {t('next')}
    </Button>
  ) : (
    <Button
      loading={disableInput}
      disabled={!canSave || disableInput}
      mode="contained"
      onPress={onSave}
    >
      {t('save')}
    </Button>
  );

  const content = (
    <>
      <View style={styles.header}>
        <Text variant="headlineLarge">
          {t(`header.${isRegistrationMode ? 'registration' : 'edit'}`)}
        </Text>
        {isRegistrationMode && (
          <Text variant="bodyLarge" style={styles.description}>
            {t('header.description')}
          </Text>
        )}
      </View>

      <View style={styles.image}>
        <AvatarWithMenu
          mediaItem={person?.profilePicture}
          disableAutoUpload={isRegistrationMode}
          onChange={setAvatarResult}
        />
      </View>

      <View style={styles.inputs}>
        <View>
          <TextInput
            style={styles.input}
            editable={!disableInput}
            mode="outlined"
            accessibilityLabel={t('firstName')}
            accessibilityHint={t('firstName')}
            label={t('firstName')}
            value={firstName ?? ''}
            onChangeText={(text) => setFirstName(text)}
            error={showFirstNameError}
            right={
              showFirstNameError && (
                <TextInput.Icon
                  icon={({ size }) => (
                    <ErrorIcon
                      fill={theme.colors.error}
                      width={size}
                      height={size}
                    />
                  )}
                />
              )
            }
          />
          {showFirstNameError && (
            <HelperText type="error">
              {t(
                !isEmptyOrNull(firstName) && firstName.length < MIN_NAME_LENGTH
                  ? 'nameError.short'
                  : 'nameError.wrongCharacters'
              )}
            </HelperText>
          )}
        </View>

        <View>
          <TextInput
            style={styles.input}
            editable={!disableInput}
            mode="outlined"
            accessibilityLabel={t('lastName')}
            accessibilityHint={t('lastName')}
            label={t('lastName')}
            value={lastName ?? ''}
            onChangeText={(text) => setLastName(text)}
            error={showLastNameError}
            right={
              showLastNameError && (
                <TextInput.Icon
                  icon={({ size }) => (
                    <ErrorIcon
                      fill={theme.colors.error}
                      width={size}
                      height={size}
                    />
                  )}
                />
              )
            }
          />
          {showLastNameError && (
            <HelperText type="error">
              {t(
                !isEmptyOrNull(lastName) && lastName.length < MIN_NAME_LENGTH
                  ? 'nameError.short'
                  : 'nameError.wrongCharacters'
              )}
            </HelperText>
          )}
        </View>
        {showPublicName && (
          <Text variant="bodyLarge" style={styles.publicName}>
            {t('public')}{' '}
            <Text style={{ fontWeight: 'bold' }}>
              {getPublicName(firstName, lastName)}
            </Text>
          </Text>
        )}

        <View>
          <TextInput
            style={styles.input}
            editable={!disableInput}
            mode="outlined"
            inputMode="numeric"
            accessibilityLabel={t('birthYear')}
            accessibilityHint={t('birthYear')}
            label={t('birthYear')}
            value={String(birthYear ?? '')}
            onChangeText={(text) => {
              const value = text.replaceAll(/\D/g, '').slice(0, 4);
              const birthYear = value === '' ? undefined : Number(value);
              setBirthYear(birthYear);
            }}
            error={showBirthError}
            right={
              showBirthError && (
                <TextInput.Icon
                  icon={({ size }) => (
                    <ErrorIcon
                      fill={theme.colors.error}
                      width={size}
                      height={size}
                    />
                  )}
                />
              )
            }
          />
          {showBirthError ? (
            <HelperText type="error">{birthErrorMessage}</HelperText>
          ) : (
            <HelperText type="info">{t('birthInfo')}</HelperText>
          )}
        </View>

        {!isRegistrationMode && (
          <>
            <Pressable
              accessibilityRole="button"
              onPress={() => handleChangeFiledData('email')}
            >
              <TextInput
                style={styles.input}
                mode="outlined"
                editable={false}
                inputMode="email"
                accessibilityLabel={t('email')}
                accessibilityHint={t('email')}
                label={t('email')}
                value={person?.email}
                contextMenuHidden
                onPress={() => handleChangeFiledData('email')}
                right={
                  isEmailVerificationEnabled && (
                    <TextInput.Icon
                      onPress={
                        isEmailVerified
                          ? showSuccessModal
                          : showVerificationModal
                      }
                      icon={() =>
                        isEmailVerified ? (
                          <VerifiedIcon
                            fill={theme.colors.tertiary}
                            width={20}
                            height={20}
                          />
                        ) : (
                          <NotVerifiedIcon
                            fill={theme.colors.error}
                            width={20}
                            height={20}
                          />
                        )
                      }
                    />
                  )
                }
              />
              {!isEmailVerified && isEmailVerificationEnabled && (
                <HelperText type="info">{t('notVerified')}</HelperText>
              )}
            </Pressable>
          </>
        )}
      </View>
    </>
  );

  if (stickyButton) {
    return (
      <View style={styles.wrapper}>
        <KeyboardAwareScrollView
          contentContainerStyle={styles.contentWrapperSticky}
          keyboardShouldPersistTaps="handled"
        >
          {content}
        </KeyboardAwareScrollView>
        <View style={styles.footer}>{submitButton}</View>
      </View>
    );
  }

  return (
    <KeyboardAwareScrollView
      style={styles.wrapper}
      contentContainerStyle={styles.contentWrapper}
      keyboardShouldPersistTaps="handled"
    >
      {content}
      {submitButton}
    </KeyboardAwareScrollView>
  );
};
